import { type ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { analytics } from '@/analytics';
import ArrowLeft from '@/assets/icons/ri_arrow_left_s_line.svg?component';
import ArrowRight from '@/assets/icons/ri_arrow_right_s_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';

import {
    breadcrumbCageLabel,
    orderedBreadcrumbCages,
    penStepHref,
    penStepNeighbors,
} from './penStep';
import type { CagesByStatus } from './types';
import { useIdlePenNeighborPrefetch, useRecordPenSwitchRender } from './useIdlePenNeighborPrefetch';

import styles from './PenStepper.module.scss';

interface PenStepperProps {
    cagesByStatus: CagesByStatus;
    cageId?: number;
    clientId?: number;
    locationId?: number;
    pagePath: string;
    className?: string;
    /** The pen menu, rendered between the two arrows so they read as one control. */
    children?: ReactNode;
}

const WIDGET_SELECTOR =
    '[role="menu"], [role="listbox"], [role="grid"], [role="slider"], [role="radio"], [role="tablist"], [role="combobox"], [role="spinbutton"], [role="tree"]';

function isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
        return false;
    }

    const tag = target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
        return true;
    }

    if (target.isContentEditable || target.getAttribute('contenteditable') === 'true') {
        return true;
    }

    return target.closest('[contenteditable="true"]') != null;
}

function isDialogTarget(target: EventTarget | null): boolean {
    return target instanceof Element && target.closest('dialog, [role="dialog"]') != null;
}

function isWidgetTarget(target: EventTarget | null): boolean {
    return target instanceof Element && target.closest(WIDGET_SELECTOR) != null;
}

function isShown(element: Element): boolean {
    if (!(element instanceof HTMLElement) || element.closest('[hidden]') != null) {
        return false;
    }

    return element.ownerDocument.defaultView?.getComputedStyle(element).display !== 'none';
}

/** An open menu or the image viewer already uses the plain arrow keys. Alt still steps. */
function plainArrowsAreTaken(event: KeyboardEvent): boolean {
    if (event.altKey) {
        return false;
    }

    const owners = document.querySelectorAll('[role="menu"], [role="listbox"], [data-arrow-nav]');
    return Array.from(owners).some(isShown);
}

function arrowDirection(event: KeyboardEvent): 'previous' | 'next' | null {
    if (event.repeat || event.metaKey || event.ctrlKey || event.shiftKey) {
        return null;
    }

    const key = event.code || event.key;
    if (key !== 'ArrowLeft' && key !== 'ArrowRight') {
        return null;
    }

    return key === 'ArrowLeft' ? 'previous' : 'next';
}

export default function PenStepper(props: PenStepperProps) {
    const { cagesByStatus, cageId, clientId, locationId, pagePath, className, children } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { search } = useLocation();
    const { enabled: tracksFishGroups } = usePostHogFeatureFlag('fish-group-tracking');

    const neighbors = penStepNeighbors(orderedBreadcrumbCages(cagesByStatus), cageId);
    const canStep = clientId != null && locationId != null;
    useIdlePenNeighborPrefetch({
        cagesByStatus,
        cageId,
        clientId,
        locationId,
        pagePath,
        search,
        tracksFishGroups,
    });
    const recordPenSwitch = useRecordPenSwitchRender({
        cageId,
        clientId,
        locationId,
        pagePath,
        search,
        tracksFishGroups,
    });

    const go = (direction: 'previous' | 'next'): 'moved' | 'end' | 'blocked' => {
        const cage = neighbors[direction];
        if (cage == null) {
            return 'end';
        }
        if (!canStep || clientId == null || locationId == null) {
            return 'blocked';
        }

        analytics.penSwitched({ source: direction });
        recordPenSwitch(direction, cage);
        navigate(
            penStepHref({
                clientId,
                locationId,
                cage,
                pagePath,
                search,
                tracksFishGroups,
            })
        );
        return 'moved';
    };

    const goRef = useRef(go);
    goRef.current = go;

    useEffect(() => {
        const step = (event: KeyboardEvent): boolean => {
            const direction = arrowDirection(event);
            if (direction == null) {
                return false;
            }
            if (
                isTypingTarget(event.target) ||
                isDialogTarget(event.target) ||
                isWidgetTarget(event.target) ||
                plainArrowsAreTaken(event)
            ) {
                return false;
            }

            const outcome = goRef.current(direction);
            if (outcome === 'blocked') {
                return false;
            }

            event.preventDefault();
            return true;
        };

        // Capture, not bubble: Alt+arrow is the browser's back/forward chord, and on
        // macOS Option rewrites `key` (the direction comes from `code`). Stopping the
        // event here also keeps a later listener from stepping twice.
        const onCapture = (event: KeyboardEvent): void => {
            if (!step(event)) {
                return;
            }
            event.stopPropagation();
        };

        const onBubble = (event: KeyboardEvent): void => {
            if (event.defaultPrevented) {
                return;
            }
            step(event);
        };

        document.addEventListener('keydown', onCapture, true);
        window.addEventListener('keydown', onBubble);
        return () => {
            document.removeEventListener('keydown', onCapture, true);
            window.removeEventListener('keydown', onBubble);
        };
    }, []);

    const labelFor = (direction: 'previous' | 'next', cageName: string | undefined): string => {
        const fishGroup = tracksFishGroups;
        if (cageName == null) {
            return fishGroup
                ? t(
                      direction === 'previous'
                          ? 'breadCrumb.component.previousFishGroup'
                          : 'breadCrumb.component.nextFishGroup'
                  )
                : t(
                      direction === 'previous'
                          ? 'breadCrumb.component.previousPen'
                          : 'breadCrumb.component.nextPen'
                  );
        }

        return fishGroup
            ? t(
                  direction === 'previous'
                      ? 'breadCrumb.component.previousFishGroupNamed'
                      : 'breadCrumb.component.nextFishGroupNamed',
                  { name: cageName }
              )
            : t(
                  direction === 'previous'
                      ? 'breadCrumb.component.previousPenNamed'
                      : 'breadCrumb.component.nextPenNamed',
                  { name: cageName }
              );
    };

    const shortcutFor = (direction: 'previous' | 'next'): string =>
        t(
            direction === 'previous'
                ? 'breadCrumb.component.previousKey'
                : 'breadCrumb.component.nextKey'
        );

    const stepButton = (direction: 'previous' | 'next') => {
        const cage = neighbors[direction];
        const label = labelFor(direction, cage ? breadcrumbCageLabel(cage) : undefined);

        return (
            <Button
                variant="ghost"
                size="small"
                iconOnly
                className={styles.step}
                disabled={cage == null || !canStep}
                aria-label={label}
                tooltip={`${label}\n${shortcutFor(direction)}`}
                tooltipPlacement="bottom"
                trackingId={direction === 'previous' ? 'pen_step_previous' : 'pen_step_next'}
                onClick={() => go(direction)}
                icon={
                    <SvgIcon
                        icon={direction === 'previous' ? ArrowLeft : ArrowRight}
                        size="small"
                        svgProps={{ 'aria-hidden': true }}
                    />
                }
            />
        );
    };

    return (
        <div className={[styles.stepper, className].filter(Boolean).join(' ')}>
            {stepButton('previous')}
            {children}
            {stepButton('next')}
        </div>
    );
}
